import BaseDocumentService from '@/services/BaseDocumentService';
import ContractAsideSignatureClient from '@/components/contract/ContractAsideSignatureClient.vue';

import { DOCUMENT_TYPE } from '@/typings/enums/DOCUMENT_TYPE';
import { getApi } from '@/utils/ApiTool';
import { Bus, itemHas, useAside, useLoader, useModal, useNotif } from '@orion.ui/orion';
import { askTempWorkerInfos, confirmPassword } from '@/utils/tools';
import { SIGNATURE_STATUS } from '@/typings/enums/SIGNATURE_STATUS';
import { isArray, uniqBy } from 'lodash-es';
import { USER_TYPE } from '@/typings/enums/USER_TYPE';
import useEmailService from '../EmailService';
import ModalOtp from '@/components/modal/ModalOtp.vue';
import { nextTick } from 'vue';
import useBusService from '../BusService';

export class ProvisionDocumentService extends BaseDocumentService<Armado.ProvisionDocumentListItem> {
	private documentType: Armado.ProvisionDocumentType;

	protected get api () {
		if (this.documentType === DOCUMENT_TYPE.ClientProvisionDocument) {
			return getApi(this.self.userType, this.documentType);
		} else {
			return getApi(this.self.userType, this.documentType);
		}
	}

	constructor (documentType: Armado.ProvisionDocumentType) {
		super();
		this.documentType = documentType;
	}


	private itemsAreProvisionDocuments (items: any[]): items is Armado.ProvisionDocumentListItem[] {
		return typeof items[0] === 'object';
	}

	async signAsync (items: {id: number}[], filterItems = true, documentType?: DOCUMENT_TYPE) {
		if (documentType) this.documentType = documentType as Armado.ProvisionDocumentType;

		if (filterItems && this.itemsAreProvisionDocuments(items)) {
			items = items.filter(x =>
				!!x.document && !x.document?.integratedDate && x.document.signatureInfoStatus !== undefined
        && (x.document.signatureInfoStatus ^ SIGNATURE_STATUS.AgencySigned) === SIGNATURE_STATUS.None,
			);
		}

		if (!items || items.length === 0) return;

		if (this.self.isTempWorker()) {
			if (items.length > 20) {
				useNotif.warning(`Attention`, `Vous ne pouvez pas signer plus de 20 contrats simultanément`);
			}

			return new Promise<boolean>(async (resolve) => {
				try {
					if (this.documentType === DOCUMENT_TYPE.TempWorkerProvisionDocument) {
						useLoader().show('Vos documents sont en cours de préparation pour la signature');
						const { data: procedure } = await getApi(USER_TYPE.TempWorker, this.documentType).getSiriusProcedure(items.map(x => x.id));

						useModal({
							Nested: ModalOtp,
							NestedProps: {
								procedure,
								ids: items.mapKey('id'),
							},
							title: 'Signature',
							size: 'lg',
							customClass: 'modal--signature',
							events: {
								'signed': (Modal, docs: {id: number, status: boolean}[]) => {
									docs.forEach(x => useBusService().emit(`temp-worker-provision-document-${x.id}:signed`, x.status));
									Bus.emit(`DashboardNewDocOrSignCardSetupService:reload`);
									setTimeout(() => Modal.close(), 3000);
									resolve(true);
								},
								'leave-start': () => {
									resolve(false);
								},
							},
						});
					}
				} catch (error: any) {
					if (isArray(error.response?.data)) {
						if (await askTempWorkerInfos(error.response.data[0])) {
							nextTick(async () => {
								await this.signAsync(items, filterItems, documentType);
							});
						}
					} else {
						useNotif.warning(`Oops`, error.response?.data);
					}
					resolve(false);
				} finally {
					useLoader().hide();
				}
			});
		} else if (this.self.isClient()) {
			if (items.length > 40) {
				useNotif.warning(`Attention`, `Vous ne pouvez pas signer plus de 40 documents simultanément`);
			}
			if (await confirmPassword()) {
				return new Promise<boolean>((resolve) => {
					useAside({
						Nested: ContractAsideSignatureClient,
						NestedProps: {
							documentIds: items.map(x => x.id),
							documentType: this.documentType,
						},
						events:
							{
								leaveStart: () => resolve(false),
								'sign': async (Aside, docs: {id: number, status: boolean}[]) => {
									try {
										if (itemHas(this.api, 'sign')) {
											await this.api.sign(docs.mapKey('id'));
											docs.forEach(x => useBusService().emit(`client-provision-document-${x.id}:signed`, x.status));
											Bus.emit(`DashboardNewDocOrSignCardSetupService:reload`);
											setTimeout(() => Aside.close(), 1000);
										}
										resolve(true);
									} catch (error: any) {
										useNotif.warning(`Oops`, error.response?.data);
										resolve(false);
									} finally {
										Aside.close();
									}
								},
							},
					});
				});
			}
		}
	}

	async sendEmailAsync (items: Armado.ProvisionDocumentListItem[]) {
		let email: Armado.Email;
		let isReminder: boolean;

		if (this.documentType === DOCUMENT_TYPE.ClientProvisionDocument) {
			email = {
				sendToUserType: USER_TYPE.Client,
				subject: '',
				body: '',
				recipients: uniqBy(
					(items as unknown as IClientProvisionDocumentItemForAgencyModel[]).map(x => ({
						id: x.clientCompanyId,
						label: x.clientCompanyName,
					})),
					'id',
				),
			};
			isReminder = items.every(x => x.document?.signatureInfoStatus &&
				(x.document.signatureInfoStatus & SIGNATURE_STATUS.ClientSigned) !== SIGNATURE_STATUS.ClientSigned);
		} else {
			email = {
				sendToUserType: USER_TYPE.TempWorker,
				subject: '',
				body: '',
				recipients: uniqBy(
					(items as unknown as ITempWorkerProvisionDocumentItemForAgencyModel[]).map(x => ({
						id: x.userId,
						label: `${x.userFirstName} ${x.userLastName}`,
					})),
					'id',
				),
			};
			isReminder = items.every(x => x.document?.signatureInfoStatus &&
				(x.document.signatureInfoStatus & SIGNATURE_STATUS.TempWorkerSigned) !== SIGNATURE_STATUS.TempWorkerSigned);
		}

		if (isReminder) {
			email.subject = `Vous avez des documents mis à disposition en attente de signature`;
			email.body = `<p>Vous avez des documents mis à disposition en attente de signature sur votre espace Armado.</p>`;
		}

		await useEmailService().sendEmailAsync(email, this.documentType, items.map(x => x.id));

		useNotif.success(`C'est parti !`, `Votre email a bien été envoyé`);

	}

}

export default function useProvisionDocumentService (documentType: Armado.ProvisionDocumentType) {
	return new ProvisionDocumentService(documentType);
}
